import React, { useMemo } from "react";
import { kebabCase, map } from "lodash";
import { Waypoint } from "react-waypoint";
import { Spacing } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { useSize } from "@lkf/hooks";
import styled, {
  ThemeProvider as StyledThemeProvider,
  x,
} from "@xstyled/styled-components";
import breakpoints from "@radarly/components/insight-pages/breakpoints";
import { Card } from "@radarly/models/cards";
import { getMaxHeightFromCards } from "@radarly/models/cards/dataviz";
import { getCardTitle } from "@radarly/models/cards/get";
import {
  oneColumnLayout,
  widthFromLayout,
} from "@radarly/models/insight-pages/layout";
import { Row, TitleRow } from "@radarly/models/insight-pages/model";
import {
  isPlaceholderRow,
  isTitleRow,
} from "@radarly/models/insight-pages/row";
import TitleRowComponent from "../title-row";

interface InsightPagesLayoutRowsProps {
  rows: Row[];
  renderCard: (card: Card, height: number, id: number) => React.ReactElement;
  renderTitle: (
    title: string,
    type: TitleRow["style"],
    id: number
  ) => React.ReactElement;
  renderPlaceHolder?: (row: Row) => React.ReactElement;
  loadMore?: () => void;
  waypointPosition?: number;
  locale: Locale;
}

const Root = styled.div`
  max-width: ${breakpoints.xl}px;
  margin: 0 auto;
  padding: 36px 0;

  [id^="row_card"] + [id^="row_title"] {
    margin-top: 36px;
  }

  [id^="row_title"] + [id^="row_card"] {
    margin-top: -4px;
  }
`;

const BREAKPOINTS_THEME = { breakpoints };

const Layout = React.memo(
  ({
    rows,
    renderCard,
    renderTitle,
    renderPlaceHolder,
    loadMore,
    waypointPosition,
    locale,
  }: InsightPagesLayoutRowsProps) => (
    <>
      {map(rows, (row, id) => (
        <x.div
          row
          px={{ xs: 0, sm: Spacing.ExtraLarge }}
          key={id}
          id={`row_${row.type}_${id}`}
        >
          {isPlaceholderRow(row) ? (
            renderPlaceHolder(row)
          ) : isTitleRow(row) ? (
            <TitleRowComponent
              row={row}
              id={id}
              locale={locale}
              renderTitle={renderTitle}
            />
          ) : (
            map(row.cards, (card, index) => (
              <x.div
                id={
                  card && card.dataviz && card.title
                    ? `widget_${card.dataviz.name}_${card.type}_${kebabCase(
                        card.title
                      )}`
                    : undefined
                }
                col={{ xs: 1, sm: widthFromLayout(row.layout, index) }}
                px={Spacing.Medium}
                my={Spacing.Medium}
                key={index}
              >
                {card
                  ? renderCard(
                      getCardTitle(card, locale) as Card,
                      getMaxHeightFromCards(row.cards),
                      id
                    )
                  : null}
              </x.div>
            ))
          )}
          {!!loadMore && waypointPosition === id && (
            <Waypoint onEnter={() => loadMore()} />
          )}
        </x.div>
      ))}
    </>
  )
);

const SmallLayout = React.memo(
  ({
    rows,
    renderCard,
    renderTitle,
    renderPlaceHolder,
    loadMore,
    waypointPosition,
    locale,
  }: InsightPagesLayoutRowsProps) => {
    const magicRows = useMemo(() => oneColumnLayout(rows), [rows]);
    return (
      <>
        <Layout
          rows={magicRows}
          renderCard={renderCard}
          renderTitle={renderTitle}
          renderPlaceHolder={renderPlaceHolder}
          loadMore={loadMore}
          waypointPosition={waypointPosition}
          locale={locale}
        />
      </>
    );
  }
);

export default function InsightPagesLayoutRows({
  rows,
  renderCard,
  renderTitle,
  renderPlaceHolder,
  loadMore,
  waypointPosition,
  locale,
}: InsightPagesLayoutRowsProps) {
  const ref = React.useRef();
  const { width } = useSize(ref);
  const isSmall = useMemo(() => width > 0 && width < 800, [width]);

  return (
    <Root ref={ref}>
      {width ? (
        <StyledThemeProvider theme={BREAKPOINTS_THEME}>
          {isSmall ? (
            <SmallLayout
              rows={rows}
              renderCard={renderCard}
              renderTitle={renderTitle}
              renderPlaceHolder={renderPlaceHolder}
              loadMore={loadMore}
              waypointPosition={waypointPosition}
              locale={locale}
            />
          ) : (
            <Layout
              rows={rows}
              renderCard={renderCard}
              renderTitle={renderTitle}
              renderPlaceHolder={renderPlaceHolder}
              loadMore={loadMore}
              waypointPosition={waypointPosition}
              locale={locale}
            />
          )}
        </StyledThemeProvider>
      ) : null}
    </Root>
  );
}
